// src/pages/Home.jsx
// Full homepage: Entry screen → Globe hero
// Vite + React — no 'use client' needed

import { useState, useEffect } from 'react';
import DottedGlobe from '../components/DottedGlobe';
import Navbar from '../components/Navbar';

export default function Home({ onLoginClick }) {
  const [entered, setEntered] = useState(false);
  const [fading, setFading]   = useState(false);

  // Preload world atlas in the background while user reads the entry screen
  useEffect(() => {
    fetch('https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json').catch(() => {});
  }, []);

  function handleEnter() {
    setFading(true);
    setTimeout(() => setEntered(true), 700); // matches CSS transition
  }

  // Allow keyboard Enter/Space to proceed
  useEffect(() => {
    if (entered) return;
    function onKey(e) {
      if (e.key === 'Enter' || e.key === ' ') handleEnter();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [entered]);

  return (
    <div style={{ background: '#000', minHeight: '100vh', fontFamily: "'DM Sans', system-ui, sans-serif" }}>

      {/* ── GOOGLE FONT ─────────────────────────────────────────── */}
      <link
        rel="stylesheet"
        href="https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;1,9..40,300&family=DM+Serif+Display:ital@0;1&display=swap"
      />

      {/* ── ENTRY SCREEN ────────────────────────────────────────── */}
      {!entered && (
        <div
          onClick={handleEnter}
          style={{
            position: 'fixed', inset: 0, zIndex: 100,
            background: '#000',
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer',
            opacity: fading ? 0 : 1,
            transition: 'opacity 0.7s ease',
            userSelect: 'none',
          }}
        >
          {/* Subtle grid texture */}
          <div style={{
            position: 'absolute', inset: 0,
            backgroundImage: 'radial-gradient(rgba(255,255,255,0.03) 1px, transparent 1px)',
            backgroundSize: '32px 32px',
            pointerEvents: 'none',
          }} />

          {/* Animated pulse dot */}
          <div style={{ marginBottom: 48, position: 'relative' }}>
            <div style={{
              width: 10, height: 10, borderRadius: '50%',
              background: '#4a90d9',
              boxShadow: '0 0 0 0 rgba(74,144,217,0.4)',
              animation: 'pulse 2s ease-in-out infinite',
            }} />
          </div>

          {/* Main wordmark */}
          <h1 style={{
            fontFamily: "'DM Serif Display', Georgia, serif",
            fontSize: 'clamp(40px, 8vw, 96px)',
            fontWeight: 400,
            color: '#fff',
            letterSpacing: '-0.02em',
            margin: 0,
            lineHeight: 1,
          }}>
            Dottedline.
          </h1>

          {/* Tagline */}
          <p style={{
            fontFamily: "'DM Sans', system-ui, sans-serif",
            fontSize: 'clamp(13px, 2vw, 18px)',
            fontWeight: 300,
            color: 'rgba(255,255,255,0.45)',
            letterSpacing: '0.25em',
            textTransform: 'uppercase',
            marginTop: 20,
          }}>
            Keep it Carrick
          </p>

          {/* Enter prompt */}
          <div style={{
            position: 'absolute', bottom: 40,
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
            animation: 'fadeFloat 2.5s ease-in-out infinite',
          }}>
            <p style={{
              fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase',
              color: 'rgba(255,255,255,0.25)', margin: 0,
            }}>
              Click anywhere to enter
            </p>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 2v12M3 9l5 5 5-5" stroke="rgba(255,255,255,0.25)" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>

          <style>{`
            @keyframes pulse {
              0%, 100% { box-shadow: 0 0 0 0 rgba(74,144,217,0.5); }
              50%       { box-shadow: 0 0 0 14px rgba(74,144,217,0); }
            }
            @keyframes fadeFloat {
              0%, 100% { opacity: 0.6; transform: translateY(0); }
              50%       { opacity: 1;   transform: translateY(-5px); }
            }
          `}</style>
        </div>
      )}

      {/* ── GLOBE HOMEPAGE ──────────────────────────────────────── */}
      {entered && (
        <div style={{
          minHeight: '100vh', display: 'flex', flexDirection: 'column',
          animation: 'fadeIn 0.8s ease forwards',
        }}>

          {/* NAV */}
          <Navbar onLoginClick={onLoginClick} variant="dark" />

          {/* GLOBE HERO */}
          <div style={{
            position: 'relative',
            width: '100%',
            height: '100dvh',
            minHeight: '-webkit-fill-available',
          }}>
            <DottedGlobe
              height={typeof window !== 'undefined' ? window.innerHeight : 660}
              isMobileOverride={typeof window !== 'undefined' && window.innerWidth < 768}
            />

            {/* Intro text — bottom left overlay, hidden on very small screens */}
            <div style={{
              position: 'absolute', bottom: 40, left: 40,
              maxWidth: 320, zIndex: 10,
              display: window.innerWidth < 480 ? 'none' : 'block',
              pointerEvents: 'none',
            }}>
              <p style={{
                fontSize: 'clamp(11px, 1.3vw, 14px)',
                color: 'rgba(255,255,255,0.5)',
                lineHeight: 1.7,
                margin: 0,
                fontWeight: 300,
                letterSpacing: '0.01em',
              }}>
                Every dot is a story.<br />
                Every arc is a connection.<br />
                These are all the places Dottedline has touched —<br />
                <span style={{ color: 'rgba(255,255,255,0.8)', fontStyle: 'italic' }}>
                  come explore with us.
                </span>
              </p>
            </div>
          </div>

          <style>{`
            @keyframes fadeIn {
              from { opacity: 0; transform: scale(0.98); }
              to   { opacity: 1; transform: scale(1); }
            }
          `}</style>
        </div>
      )}
    </div>
  );
}
